import { DOCUMENT } from '@angular/common';
import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { CwActionIcon, CwIcon } from '@bbraun/cortex-angular';

import { TranslationService } from '../../../shell/translation-service';
import { SidebarNavigationState } from '../sidebar-navigation-state';

const CLOCK_TICK_INTERVAL_MS = 30_000;

@Component({
  selector: 'ima-main-footer',
  imports: [CwActionIcon, CwIcon],
  templateUrl: './main-footer.html',
  styleUrl: './main-footer.scss',
})
export class MainFooter {
  protected readonly stockConflictCount = signal(4);
  protected readonly i18n = inject(TranslationService);
  protected readonly workstationName = computed(() => this.i18n.t('footer.workstation'));
  private readonly sidebarState = inject(SidebarNavigationState);
  protected readonly isSidebarCollapsed = computed(() => !this.sidebarState.visible());
  private readonly now = signal(new Date());

  private readonly localeTag = computed(() => this.i18n.locale());

  // Placeholders until a real device/inventory data source exists (see IMA-1408).

  protected readonly weekday = computed(() =>
    new Intl.DateTimeFormat(this.localeTag(), { weekday: 'long' }).format(this.now()),
  );

  protected readonly date = computed(() =>
    new Intl.DateTimeFormat(this.localeTag(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(this.now()),
  );

  protected readonly time = computed(() =>
    new Intl.DateTimeFormat(this.localeTag(), {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(this.now()),
  );

  private readonly document = inject(DOCUMENT);

  constructor() {
    const view = this.document.defaultView;
    const intervalId = view?.setInterval(() => {
      this.now.set(new Date());
    }, CLOCK_TICK_INTERVAL_MS);

    inject(DestroyRef).onDestroy(() => {
      if (intervalId !== undefined) {
        view?.clearInterval(intervalId);
      }
    });
  }

  protected toggleSidebar(): void {
    this.sidebarState.toggle();
  }
}
